import { FileText, ShoppingBag, CreditCard, Truck, RefreshCw, AlertCircle, Scale } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export default function TermsPage() {
  const { language } = useAuth();

  const sections = [
    {
      icon: ShoppingBag,
      title: language === 'hi' ? 'ऑर्डर और खरीदारी' : 'Orders & Purchases',
      content:
        language === 'hi'
          ? [
              'सभी ऑर्डर उपलब्धता और पुष्टि के अधीन हैं',
              'कीमतें बिना पूर्व सूचना के बदल सकती हैं',
              'हम किसी भी ऑर्डर को अस्वीकार करने का अधिकार सुरक्षित रखते हैं',
              'न्यूनतम ऑर्डर राशि लागू हो सकती है',
              'उत्पाद की तस्वीरें केवल संदर्भ के लिए हैं',
            ]
          : [
              'All orders are subject to availability and confirmation',
              'Prices are subject to change without prior notice',
              'We reserve the right to refuse any order',
              'Minimum order amount may apply',
              'Product images are for reference only',
            ],
    },
    {
      icon: CreditCard,
      title: language === 'hi' ? 'भुगतान' : 'Payment',
      content:
        language === 'hi'
          ? [
              'वर्तमान में केवल कैश ऑन डिलीवरी (COD) उपलब्ध है',
              'डिलीवरी के समय सही राशि रखें',
              'डिलीवरी पर्ची को रसीद के रूप में रखें',
              'ऑनलाइन पेमेंट जल्द ही उपलब्ध होगा',
            ]
          : [
              'Currently only Cash on Delivery (COD) is available',
              'Please keep exact amount ready at delivery',
              'Keep delivery slip as receipt',
              'Online payment coming soon',
            ],
    },
    {
      icon: Truck,
      title: language === 'hi' ? 'डिलीवरी' : 'Delivery',
      content:
        language === 'hi'
          ? [
              'डिलीवरी केवल चयनित पिनकोड में उपलब्ध है',
              'डिलीवरी का समय अनुमानित है और बदल सकता है',
              'डिलीवरी के समय कोई घर पर होना आवश्यक है',
              '₹500 से अधिक ऑर्डर पर मुफ्त डिलीवरी',
              'खराब मौसम में डिलीवरी में देरी हो सकती है',
            ]
          : [
              'Delivery available only in selected pincodes',
              'Delivery time is estimated and may vary',
              'Someone must be present at delivery',
              'Free delivery on orders above ₹500',
              'Delivery may be delayed in bad weather',
            ],
    },
    {
      icon: RefreshCw,
      title: language === 'hi' ? 'रिटर्न और रिफंड' : 'Returns & Refunds',
      content:
        language === 'hi'
          ? [
              'खराब या गलत उत्पाद पर रिटर्न स्वीकार किए जाते हैं',
              'डिलीवरी के 24 घंटे के भीतर शिकायत दर्ज करें',
              'रिफंड अगले ऑर्डर में क्रेडिट के रूप में दिया जाएगा',
              'पैकेज खोलने के बाद कुछ उत्पाद वापस नहीं होंगे',
              'फल और सब्जियां स्वाद के आधार पर वापस नहीं होंगी',
            ]
          : [
              'Returns accepted for damaged or incorrect products',
              'Report issues within 24 hours of delivery',
              'Refund will be given as credit for next order',
              'Some products cannot be returned once opened',
              'Fruits and vegetables not returnable based on taste',
            ],
    },
    {
      icon: AlertCircle,
      title: language === 'hi' ? 'उपयोगकर्ता की जिम्मेदारियां' : 'User Responsibilities',
      content:
        language === 'hi'
          ? [
              'सही डिलीवरी पता और फ़ोन नंबर प्रदान करें',
              'एक व्यक्ति एक खाता ही बना सकता है',
              'अपने खाते की जानकारी सुरक्षित रखें',
              'किसी भी अनधिकृत उपयोग की रिपोर्ट तुरंत करें',
            ]
          : [
              'Provide correct delivery address and phone number',
              'One person can create only one account',
              'Keep your account information secure',
              'Report any unauthorized use immediately',
            ],
    },
    {
      icon: Scale,
      title: language === 'hi' ? 'दायित्व की सीमा' : 'Limitation of Liability',
      content:
        language === 'hi'
          ? [
              'हम अप्रत्यक्ष नुकसान के लिए जिम्मेदार नहीं हैं',
              'उत्पाद की जानकारी यथासंभव सटीक है',
              'तकनीकी समस्याओं के लिए क्षमा करें',
              'विवाद शामली न्यायालय के अधीन होंगे',
            ]
          : [
              'We are not liable for indirect damages',
              'Product information is as accurate as possible',
              'We apologize for technical issues',
              'Disputes subject to Shamli court jurisdiction',
            ],
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="terms-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-green-600 to-green-700 text-white py-16">
        <div className="container-app">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-10 h-10" />
            <h1 className="text-3xl md:text-4xl font-bold">
              {language === 'hi' ? 'नियम व शर्तें' : 'Terms & Conditions'}
            </h1>
          </div>
          <p className="text-green-100 text-lg max-w-2xl">
            {language === 'hi'
              ? 'AV Mart Shamli का उपयोग करके, आप इन नियमों और शर्तों से सहमत होते हैं।'
              : 'By using AV Mart Shamli, you agree to these terms and conditions.'}
          </p>
          <p className="text-green-200 text-sm mt-4">
            {language === 'hi'
              ? 'अंतिम अपडेट: जनवरी 2025'
              : 'Last Updated: January 2025'}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="container-app">
          <div className="max-w-4xl mx-auto space-y-8">
            {sections.map((section, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <section.icon className="w-5 h-5 text-green-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-slate-900">{section.title}</h2>
                </div>
                <ul className="space-y-2">
                  {section.content.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-slate-600">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            {/* Acceptance */}
            <div className="bg-green-50 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">
                {language === 'hi' ? 'स्वीकृति' : 'Acceptance'}
              </h2>
              <p className="text-slate-600">
                {language === 'hi'
                  ? 'AV Mart Shamli पर ऑर्डर करके, आप इन नियमों और शर्तों से सहमत होते हैं। यदि आप किसी शर्त से सहमत नहीं हैं, तो कृपया हमारी सेवाओं का उपयोग न करें।'
                  : 'By placing an order on AV Mart Shamli, you agree to these terms and conditions. If you do not agree with any term, please do not use our services.'}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
